import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../utils/logger';
import { buildDetectionQuery, getMarketOverride } from '../utils/marketOverride';

const CountryContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = {
  UK: { code: 'UK', flag: 'https://flagcdn.com/w40/gb.png', name: 'United Kingdom', currency: 'GBP', symbol: '\u00A3', locale: 'en-GB' },
  IN: { code: 'IN', flag: 'https://flagcdn.com/w40/in.png', name: 'India', currency: 'INR', symbol: '\u20B9', locale: 'en-IN' },
  CA: { code: 'CA', flag: 'https://flagcdn.com/w40/ca.png', name: 'Canada', currency: 'CAD', symbol: 'C$', locale: 'en-CA' },
};

const DEFAULT_FEES = {
  UK: { seller: 45, sellerRent: 5, buyer: 20, pro: 25, tenant: 0, tenantNote: 'Tenant Fees Act 2019' },
  IN: { seller: 2999, sellerRent: 499, buyer: 999, pro: 1499, tenant: 500, tenantNote: 'Platform Access Fee' },
  CA: { seller: 55, sellerRent: 10, buyer: 25, pro: 30, tenant: 15, tenantNote: 'Platform Access Fee' },
};

const GEO_MAP = { GB: 'UK', IN: 'IN', CA: 'CA' };

export const CountryProvider = ({ children }) => {
  const [country, setCountryState] = useState(() => {
    return localStorage.getItem('shopperden_country') || 'UK';
  });
  const [detectedCountry, setDetectedCountry] = useState(() => {
    // Survive StrictMode remounts — detection result is also persisted
    return localStorage.getItem('shopperden_country_detected') || null;
  });
  const [showWelcome, setShowWelcome] = useState(false);
  const [adminFees, setAdminFees] = useState(null);
  const [freeTrial, setFreeTrial] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({ GBP: 1, INR: 106, CAD: 1.72 });

  // Fetch admin-configured fees from the public endpoint
  useEffect(() => {
    const fetchFees = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fees`);
        if (res.ok) {
          const data = await res.json();
          setAdminFees(data);
          setFreeTrial(data.free_trial_enabled || false);
          if (data.exchange_rates) setExchangeRates(data.exchange_rates);
        }
      } catch (err) { logger.error('CountryContext: failed to load fees:', err); }
    };
    fetchFees();
  }, []);

  // Auto-detect country on first visit — prefer the server-side endpoint
  // (Cloudflare cf-ipcountry header → no CORS, no rate limits) and fall back
  // to ipapi.co only if the backend doesn't recognise the visitor.
  useEffect(() => {
    const hasChosen = localStorage.getItem('shopperden_country_chosen');
    const override = getMarketOverride();
    // Admin/owner `?test=IN` always wins — even when the user already
    // committed to a country earlier in the session.
    if (hasChosen && !override) return;

    const applyDetection = (mapped) => {
      if (mapped && COUNTRIES[mapped]) {
        setDetectedCountry(mapped);
        setCountryState(mapped);
        localStorage.setItem('shopperden_country', mapped);
      }
    };

    const detectCountry = async () => {
      // Admin/owner test override (`?test=IN`) wins instantly so the
      // platform owner can validate IN/CA from a UK location without VPN.
      const override = getMarketOverride();
      if (override) {
        applyDetection(override);
        setShowWelcome(true);
        return;
      }

      // Browser timezone + tz/admin overrides are bundled via the helper
      // so the backend receives every detection signal we have.
      const detectQuery = buildDetectionQuery();

      // 1) Try the backend (Cloudflare cf-ipcountry header — passes when
      //    ingress preserves it; ip-api.com fallback baked in server-side;
      //    final timezone fallback added 2026-02-17 so shopperden.com
      //    correctly detects IN/CA even when CF headers are stripped.)
      try {
        const res = await fetch(`${API_URL}/api/geo/country${detectQuery}`, { signal: AbortSignal.timeout(4000) });
        if (res.ok) {
          const data = await res.json();
          if (data?.region) {
            applyDetection(data.region);
            setShowWelcome(true);
            return;
          }
        }
      } catch (err) {
        logger.debug?.('CountryContext: backend geo unavailable, trying CF trace');
      }
      // 2) Cloudflare /cdn-cgi/trace — same-origin, no CORS, no rate limits.
      //    Available on every Cloudflare-fronted domain (production shopperden.com).
      try {
        const res = await fetch('/cdn-cgi/trace', { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const text = await res.text();
          const match = text.match(/^loc=([A-Z]{2})$/m);
          if (match) {
            applyDetection(GEO_MAP[match[1]]);
            if (GEO_MAP[match[1]]) {
              setShowWelcome(true);
              return;
            }
          }
        }
      } catch (err) {
        logger.debug?.('CountryContext: CF trace unavailable');
      }
      // 3) Last fallback — third-party ipapi.co (rate-limited but free)
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
        const data = await res.json();
        applyDetection(GEO_MAP[data?.country_code]);
      } catch (err) {
        logger.debug?.('CountryContext: ipapi unavailable, using country picker');
      }
      setShowWelcome(true);
    };
    detectCountry();
  }, []);

  const setCountry = useCallback((code) => {
    if (COUNTRIES[code]) {
      setCountryState(code);
      localStorage.setItem('shopperden_country', code);
      localStorage.setItem('shopperden_country_chosen', 'true');
    }
  }, []);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    localStorage.setItem('shopperden_country_chosen', 'true');
    // Detection-badge is one-shot — clear it once user has acknowledged.
    localStorage.removeItem('shopperden_country_detected');
    setDetectedCountry(null);
  }, []);

  const countryInfo = COUNTRIES[country] || COUNTRIES.UK;

  // Use admin fees when admin country matches selected country; else use defaults
  const fees = useMemo(() => {
    const base = DEFAULT_FEES[country] || DEFAULT_FEES.UK;
    if (adminFees && adminFees.country === country) {
      return {
        seller: adminFees.seller_match_fee ?? base.seller,
        sellerRent: adminFees.seller_rent_fee ?? base.sellerRent,
        buyer: adminFees.buyer_verification_fee ?? base.buyer,
        pro: adminFees.professional_access_fee ?? base.pro,
        tenant: adminFees.tenant_fee ?? base.tenant,
        tenantNote: base.tenantNote,
      };
    }
    return base;
  }, [country, adminFees]);

  const formatPrice = useCallback((price) => {
    if (!price && price !== 0) return countryInfo.symbol + '0';
    return countryInfo.symbol + new Intl.NumberFormat(countryInfo.locale, { maximumFractionDigits: 0 }).format(price);
  }, [countryInfo]);

  const contextValue = useMemo(() => ({
    country, setCountry, countryInfo, fees, formatPrice, exchangeRates,
    countries: COUNTRIES, detectedCountry, showWelcome, dismissWelcome, freeTrial,
  }), [country, setCountry, countryInfo, fees, formatPrice, exchangeRates, detectedCountry, showWelcome, dismissWelcome, freeTrial]);

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error('useCountry must be used within CountryProvider');
  return ctx;
};
